import React, { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { signalRService, SignalRService } from '../services/signalrService';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { isHelpdeskRoute } from '../lib/helpdesk-utils';
import { toast } from '../hooks/use-toast';
import { queryKeys } from '../lib/queryKeys';
import type { NotificationResponse, NotificationListResponse } from '../types/notifications';
import { getNotificationTypeLabel } from '../types/notifications';

interface SignalRContextType {
  signalR: SignalRService;
  isConnected: boolean;
  connectionState: { ticket: string; notification: string };
}

const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

interface SignalRProviderProps {
  children: ReactNode;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({ children }) => {
  const { token, isAuthenticated, currentOrganization } = useAuth();
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState({ ticket: 'Disconnected', notification: 'Disconnected' });
  const notificationHandlerRef = useRef<(() => void) | null>(null);
  const markedAsReadHandlerRef = useRef<(() => void) | null>(null);

  // Helper to update connection state
  const updateConnectionState = () => {
    const connected = signalRService.isConnected;
    const state = signalRService.connectionState;
    setIsConnected(connected);
    setConnectionState(state);
  };

  useEffect(() => {
    let cleanupConnectionCallback: (() => void) | undefined;

    const initializeSignalR = async () => {
      // Only initialize SignalR on helpdesk routes, not customer portal routes
      const currentPath = window.location.pathname;
      const shouldConnect = isAuthenticated && token && isHelpdeskRoute(currentPath);

      if (shouldConnect) {
        try {
          await signalRService.initialize(token);

          // Subscribe to connection state changes for immediate updates
          cleanupConnectionCallback = signalRService.onConnectionStateChange(updateConnectionState);

          // Check immediately after initialization
          updateConnectionState();
        } catch (error) {
          console.error('Failed to initialize SignalR:', error);
        }
      } else {
        // Disconnect if not authenticated or not on helpdesk route
        if (signalRService.isConnected) {
          await signalRService.disconnect();
          setIsConnected(false);
          setConnectionState({ ticket: 'Disconnected', notification: 'Disconnected' });
        }
      }
    };

    initializeSignalR();

    return () => {
      cleanupConnectionCallback?.();
    };
  }, [isAuthenticated, token]);

  // Listen for route changes to connect/disconnect SignalR appropriately
  useEffect(() => {
    const handleLocationChange = () => {
      const currentPath = window.location.pathname;
      const shouldConnect = isAuthenticated && token && isHelpdeskRoute(currentPath);

      if (shouldConnect && !signalRService.isConnected) {
        // We're now on a helpdesk route but not connected - connect
        signalRService.initialize(token!)
          .then(() => {
            updateConnectionState();
          })
          .catch(error => {
            console.error('Failed to initialize SignalR on route change:', error);
          });
      } else if (!shouldConnect && signalRService.isConnected) {
        // We're not on a helpdesk route but are connected - disconnect
        signalRService.disconnect()
          .then(() => {
            setIsConnected(false);
            setConnectionState({ ticket: 'Disconnected', notification: 'Disconnected' });
          });
      }
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleLocationChange);

    // Listen for pushstate/replacestate (programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args: Parameters<typeof originalPushState>) {
      originalPushState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };

    history.replaceState = function(...args: Parameters<typeof originalReplaceState>) {
      originalReplaceState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [isAuthenticated, token]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      signalRService.disconnect();
    };
  }, []);

  // Set up InAppNotification handler when connected
  useEffect(() => {
    if (isConnected && !notificationHandlerRef.current) {
      const handleInAppNotification = (notification: NotificationResponse) => {
        // Optimistically update the notification list cache
        // This ensures the list is instantly updated even when dropdown is closed
        queryClient.setQueryData(
          queryKeys.notifications.list({ pageSize: 5, page: 1 }),
          (oldData: NotificationListResponse | undefined) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              notifications: [notification, ...oldData.notifications.slice(0, 4)],
              totalCount: oldData.totalCount + 1,
              unreadCount: oldData.unreadCount + 1,
            };
          }
        );

        // Invalidate count query to ensure accurate badge
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.count() });

        // Mark list queries as stale for eventual consistency (won't refetch if disabled)
        queryClient.invalidateQueries({
          queryKey: queryKeys.notifications.lists(),
          refetchType: 'none',
        });

        // Show a toast notification
        toast({
          title: getNotificationTypeLabel(notification.type),
          description: notification.title,
          duration: 5000,
        });
      };

      // Store the cleanup function in the ref
      notificationHandlerRef.current = signalRService.on('InAppNotification', handleInAppNotification);
    }

    // Clean up handler when disconnecting
    if (!isConnected && notificationHandlerRef.current) {
      notificationHandlerRef.current();
      notificationHandlerRef.current = null;
    }
  }, [isConnected, queryClient]);

  // Set up NotificationsMarkedAsRead handler for cross-device sync
  useEffect(() => {
    if (isConnected && !markedAsReadHandlerRef.current) {
      const handleNotificationsMarkedAsRead = () => {
        console.log('[SignalR] NotificationsMarkedAsRead received - syncing notification state');
        // Invalidate notification queries to refresh the bell badge and lists
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      };

      // Store the cleanup function in the ref
      markedAsReadHandlerRef.current = signalRService.on('NotificationsMarkedAsRead', handleNotificationsMarkedAsRead);
    }

    // Clean up handler when disconnecting
    if (!isConnected && markedAsReadHandlerRef.current) {
      markedAsReadHandlerRef.current();
      markedAsReadHandlerRef.current = null;
    }
  }, [isConnected, queryClient]);

  // Global ticket event handlers - always active regardless of current page
  // This ensures cache is invalidated even when user is on a different route
  useEffect(() => {
    if (!isConnected) return;

    // Define event data types locally to avoid import issues
    interface TicketUpdatedData {
      ticketId?: string;
      TicketId?: string;
    }

    interface CommentAddedData {
      ticketId?: string;
      TicketId?: string;
    }

    interface ParticipantsChangedData {
      ticketId?: string;
      TicketId?: string;
    }

    interface FollowersChangedData {
      ticketId?: string;
      TicketId?: string;
    }

    interface CommentAttachmentAddedData {
      ticketId?: string;
      TicketId?: string;
    }

    const handleGlobalTicketCreated = () => {
      console.log('[SignalR Global] Ticket created - invalidating caches');
      // Invalidate all ticket lists and dashboard stats
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.tickets.allOrgsLists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
    };

    const handleGlobalTicketUpdate = (data: TicketUpdatedData) => {
      const ticketId = data.ticketId || data.TicketId;
      console.log('[SignalR Global] Ticket updated - invalidating caches', { ticketId });

      setTimeout(() => {
        // Invalidate all lists and dashboard
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.lists() });
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.allOrgsLists() });
        queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });

        // Invalidate specific ticket if ID available
        if (ticketId) {
          queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(ticketId) });
        }
      }, 250);
    };

    const handleGlobalCommentAdded = (data: CommentAddedData) => {
      const ticketId = data.ticketId || data.TicketId;
      console.log('[SignalR Global] Comment added - invalidating caches', { ticketId });

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.lists() });
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.allOrgsLists() });
        queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });

        if (ticketId) {
          queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(ticketId) });
        }
      }, 250);
    };

    const handleGlobalParticipantsChanged = (data: ParticipantsChangedData) => {
      const ticketId = data.ticketId || data.TicketId;
      console.log('[SignalR Global] Participants changed - invalidating caches', { ticketId });

      setTimeout(() => {
        // Invalidate ticket lists (participant count may be shown)
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.lists() });
        queryClient.invalidateQueries({ queryKey: queryKeys.tickets.allOrgsLists() });

        if (ticketId) {
          queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(ticketId) });
        }
      }, 250);
    };

    const handleGlobalFollowersChanged = (data: FollowersChangedData) => {
      const ticketId = data.ticketId || data.TicketId;
      console.log('[SignalR Global] Followers changed - invalidating caches', { ticketId });

      setTimeout(() => {
        if (ticketId) {
          queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(ticketId) });
        }
      }, 250);
    };

    const handleGlobalCommentAttachmentAdded = (data: CommentAttachmentAddedData) => {
      const ticketId = data.ticketId || data.TicketId;
      console.log('[SignalR Global] Comment attachment added - invalidating caches', { ticketId });

      setTimeout(() => {
        if (ticketId) {
          queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
          queryClient.invalidateQueries({ queryKey: queryKeys.tickets.history(ticketId) });
        }
      }, 250);
    };

    // Register global handlers for all ticket-related events
    const cleanups = [
      signalRService.on('TicketCreated', handleGlobalTicketCreated),
      signalRService.on('TicketUpdate', handleGlobalTicketUpdate),
      signalRService.on('TicketUpdated', handleGlobalTicketUpdate),
      signalRService.on('TicketActivity', handleGlobalTicketUpdate),
      signalRService.on('CommentAdded', handleGlobalCommentAdded),
      signalRService.on('ParticipantsChanged', handleGlobalParticipantsChanged),
      signalRService.on('FollowersChanged', handleGlobalFollowersChanged),
      signalRService.on('CommentAttachmentAdded', handleGlobalCommentAttachmentAdded),
    ];

    return () => {
      cleanups.forEach(cleanup => cleanup());
    };
  }, [isConnected, queryClient]);

  // The server revoked this user's session (removed from or deactivated in an organization, account deactivated,
  // password reset). End it now instead of waiting for the next request to fail. An organizationId names the one
  // organization affected; sessions in the user's other organizations carry on.
  useEffect(() => {
    if (!isConnected) return;

    return signalRService.on('SessionRevoked', (data: { organizationId?: string | null }) => {
      if (data?.organizationId && currentOrganization && data.organizationId !== currentOrganization.id) return;
      apiService.endSession();
    });
  }, [isConnected, currentOrganization]);

  const contextValue: SignalRContextType = {
    signalR: signalRService,
    isConnected,
    connectionState,
  };

  return (
    <SignalRContext.Provider value={contextValue}>
      {children}
    </SignalRContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSignalR = (): SignalRContextType => {
  const context = useContext(SignalRContext);
  if (context === undefined) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return context;
};